import React from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import styled from "styled-components/macro";
import Constants from "../../constants";
import { getSideMenuOpen, getAppConfig, getLanguage, getTouchScreen } from "../../store/sagas/selectors";
import { useBreakpoint } from "../../utilities/media-js-breakpoints";

import LayoutIcon from "../../icons/LayoutIcon.svg";
import FavoriteIcon from "../../icons/Favorite.svg";
import MyFavoritesIcon from "../../icons/MyFavorites.svg";

const Container = styled.div<ContainerProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    bottom: 165px;
    right: ${props => (props.expanded ? props.sideMenuWidth : props.right)}px;
    top: auto;
    position: absolute;
    z-index: 4;
    max-width: 45px;
    overflow: hidden;
    transition: right 0.45s;
    @media (hover) {
        &:hover {
            max-width: 400px;
        }
    }
    ${({ theme }) => theme.bp.xxxl`
        transform: scale(1.85, 1.85);
        transform-origin: bottom right;
        bottom: 260px;
        max-width: 250px;
    `}
`;

const MobileContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: -60px;
    z-index: 4;
    overflow: hidden;
    transition: right 0.45s;
    pointer-events: none;
`;

const Icon = styled.div`
    width: 40px;
    height: 40px;
`;

const Text = styled.div`
    width: auto;
    height: 40px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    margin-right: 5px;
    padding: 0px 10px;
    word-wrap: nowrap;
    color: white;
    text-shadow: 0px 1px 12px rgba(0, 0, 0, 1);
    opacity: 0;
    transition: all 0.4s ease-in-out;
`;

const SvgImg = styled.div<SVGProps>`
    border: none;
    width: 40px;
    height: 40px;
    filter: drop-shadow(0px 0px 2px rgb(0 0 0 / 0.4));
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    svg {
        width: 30px;
        height: 30px;
        transition: all 0.2s ease-in-out;
        fill: currentColor;
        color: ${props => (props.active ? "var(--primary-color)" : "var(--light-active-color)")};
        @media (hover) {
            &:hover {
                color: ${props => (props.active ? "var(--primary-hover-color)" : "var(--light-hover-color)")};
                cursor: pointer;
                filter: drop-shadow(0px 0px 2px rgb(0 0 0 / 0.5));
            }
        }
    }
`;

const ButtonContainer = styled.button<ButtonContainerProps>`
    height: 40px;
    background: transparent;
    margin: ${props => (props.isMobile ? "5px 5px" : "10px 0px")};
    padding: 0px;
    overflow: hidden;
    transition: all 0.2s ease-in-out;
    border: none;
    ${props => (props.isMobile ? "pointer-events: all;" : "")}

    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-direction: row;

    &:focus {
        ${props => (props.isTouchScreen ? "outline: none;" : "")}
    }

    @media (hover) {
        &:hover ${Text} {
            cursor: pointer;
            opacity: 1;
        }
    }
`;

interface ContainerProps {
    sideMenuWidth: number;
    right: number;
    expanded: Boolean;
}

interface SVGProps {
    active?: Boolean;
}

interface ButtonContainerProps {
    isMobile: Boolean;
    isTouchScreen: Boolean;
}

export const FloatMenu = ({
    menuSideRight,
    onLayout,
    addFavorite,
    onFavorite,
    currentConfigInFavorites,
    hasFavorites,
    hasLayouts,
    showFavorites,
}: {
    menuSideRight: Boolean;
    onLayout: Function;
    addFavorite: Function;
    onFavorite: Function;
    currentConfigInFavorites: Boolean;
    hasFavorites: Boolean;
    hasLayouts: Boolean;
    showFavorites: Boolean;
}) => {
    const breakpoints = useBreakpoint();
    const isOpen: boolean = useSelector(getSideMenuOpen);
    const appConfig = useSelector(getAppConfig);
    const language = useSelector(getLanguage);
    const touchScreen = useSelector(getTouchScreen);
    const marginModifier = breakpoints.xxxl ? 10 : 0;
    const isHighRes = breakpoints.xxxl
        ? Constants.highResSideMenuWidth + 70 + marginModifier
        : Constants.sideMenuWidth + 70;
    const isMobileOrTablet = breakpoints.sm || breakpoints.xs ? 0 : 70 + marginModifier;
    const isMobile = breakpoints.sm || breakpoints.xs;

    const RenderButtons = () => {
        return (
            <>
                {hasFavorites ? (
                    <ButtonContainer
                        isMobile={isMobile}
                        isTouchScreen={touchScreen}
                        onClick={() => {
                            onFavorite();
                        }}
                    >
                        {!isMobile ? (
                            <Text>{get(appConfig, `translations.${language}.favorites`, "favorites")}</Text>
                        ) : null}
                        <Icon>
                            <SvgImg>
                                <MyFavoritesIcon />
                            </SvgImg>
                        </Icon>
                    </ButtonContainer>
                ) : null}

                {showFavorites ? (
                    <ButtonContainer
                        isMobile={isMobile}
                        isTouchScreen={touchScreen}
                        onClick={() => {
                            addFavorite();
                        }}
                    >
                        {!isMobile ? (
                            <Text>
                                {currentConfigInFavorites
                                    ? get(appConfig, `translations.${language}.removeFavorite`, "removeFavorite")
                                    : get(appConfig, `translations.${language}.addFavorite`, "addFavorite")}
                            </Text>
                        ) : null}
                        <Icon>
                            <SvgImg active={currentConfigInFavorites}>
                                <FavoriteIcon />
                            </SvgImg>
                        </Icon>
                    </ButtonContainer>
                ) : null}

                {hasLayouts ? (
                    <ButtonContainer
                        isMobile={isMobile}
                        isTouchScreen={touchScreen}
                        onClick={() => {
                            onLayout();
                        }}
                    >
                        {!isMobile ? (
                            <Text>{get(appConfig, `translations.${language}.layouts`, "layouts")}</Text>
                        ) : null}
                        <Icon>
                            <SvgImg>
                                <LayoutIcon />
                            </SvgImg>
                        </Icon>
                    </ButtonContainer>
                ) : null}
            </>
        );
    };

    return (
        <>
            {isMobile ? (
                <MobileContainer>{RenderButtons()}</MobileContainer>
            ) : (
                <Container sideMenuWidth={isHighRes} right={isMobileOrTablet} expanded={isOpen && menuSideRight}>
                    {RenderButtons()}
                </Container>
            )}
        </>
    );
};
